import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'
import { ChartSkeleton } from './ChartSkeleton'
import { ChartError } from './ChartError'
import { EmptyChart } from './EmptyChart'
import { usePromotionsByYear } from '../hooks/usePromotionsByYear'
import { PopulationSheet } from './PopulationSheet'
import { useState } from 'react'

export function PromotionsByYearChart({ className }: { className?: string }) {
  const { data, isLoading, error } = usePromotionsByYear()
  const [sheetOpen, setSheetOpen] = useState(false)
  // TODO: Replace with real role check
  const canEdit = true

  if (isLoading) return <ChartSkeleton className={className} />
  if (error) return <ChartError className={className} />
  if (!data?.length) return <EmptyChart message="No promotion data available" className={className} />

  // Prepare chart data with percentage
  const chartData = data.map(d => ({
    year: d.year,
    percentage: (typeof d.promotions === 'number' && typeof d.engineers === 'number' && d.engineers)
      ? (d.promotions / d.engineers) * 100
      : null,
    promotions: d.promotions,
    engineers: d.engineers,
  }))

  const chartConfig = {
    percentage: {
      label: '% Promoted',
      color: 'hsl(var(--chart-1))',
    },
  }

  return (
    <div className={className}>
      <div className="mb-4">
        <h3 className="text-xl font-semibold">Promotions by Year</h3>
        <p className="text-base text-muted-foreground">Promotions as a percentage of population last 5 years</p>
      </div>
      <div className="h-[300px]">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData} margin={{ top: 5, right: 10, left: 10, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
            <XAxis dataKey="year" className="text-xs" />
            <YAxis
              className="text-xs"
              domain={[0, 100]}
              tickFormatter={v => `${v}%`}
              label={{ value: '% Promoted', angle: -90, position: 'insideLeft', offset: 10 }}
            />
            <Tooltip
              content={({ active, payload, label }) => {
                if (active && payload && payload.length) {
                  const d = payload[0].payload
                  return (
                    <div className="rounded-lg border bg-background p-2 shadow-md">
                      <div className="flex flex-col gap-2">
                        <span className="text-xs font-semibold mb-1">{label}</span>
                        <div className="flex flex-row items-center justify-between w-full text-xs gap-4">
                          <span>% Promoted</span>
                          <span className="font-bold">{d.percentage !== null ? `${d.percentage.toFixed(1)}%` : 'N/A'}</span>
                        </div>
                        <div className="flex flex-row items-center justify-between w-full text-xs gap-4">
                          <span>Promotions</span>
                          <span className="font-bold">{d.promotions}</span>
                        </div>
                        <div className="flex flex-row items-center justify-between w-full text-xs gap-4">
                          <span>Engineers</span>
                          <span className="font-bold">{d.engineers ?? 'N/A'}</span>
                        </div>
                      </div>
                    </div>
                  )
                }
                return null
              }}
            />
            <Line
              type="monotone"
              dataKey="percentage"
              stroke={chartConfig.percentage.color}
              strokeWidth={2}
              dot={{ fill: chartConfig.percentage.color }}
              activeDot={{ r: 6 }}
              connectNulls
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
      {canEdit && (
        <div className="flex justify-end mt-2">
          <button
            type="button"
            className="text-primary underline text-sm hover:text-primary/80"
            onClick={() => setSheetOpen(true)}
          >
            Edit aggregates
          </button>
        </div>
      )}
      <PopulationSheet open={sheetOpen} onOpenChange={setSheetOpen} />
    </div>
  )
} 